// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    headingMobile = "headingMobile",
    minimumHeight = "minimumHeight",
    subheading = "subheading",
    subheadingMobile = "subheadingMobile",
    checkmarkColor = "checkmarkColor",
    buttonLabel = "buttonLabel",
    buttonClassName = "buttonClassName",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    partialOverlay = "partialOverlay",
    partialOverlayPositioning = "partialOverlayPositioning",
    disableButton = "disableButton",
    bottomContentPadding = "bottomContentPadding",
    topContentPadding = "topContentPadding",
    contentAlignment = "contentAlignment",
    rightContentPadding = "rightContentPadding",
    leftContentPadding = "leftContentPadding",
    entireBannerLink = "entireBannerLink",
    showInTwoColumn = "showInTwoColumn",
    smallImage = "smallImage",
    showBackgroundColor = "showBackgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.headingMobile]: string;
        [fields.minimumHeight]: number;
        [fields.checkmarkColor]: string;
        [fields.subheading]: string;
        [fields.subheadingMobile]: string;
        [fields.buttonLabel]: string;
        [fields.buttonClassName]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.imageOverlay]: string;
        [fields.partialOverlay]: boolean;
        [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.disableButton]: boolean;
        [fields.bottomContentPadding]: number;
        [fields.topContentPadding]: number;
        [fields.rightContentPadding]: number;
        [fields.leftContentPadding]: number;
        [fields.contentAlignment]: "left" | "right";
        [fields.entireBannerLink]: LinkFieldValue;
        [fields.showInTwoColumn]: boolean;
        [fields.smallImage]: string;
        [fields.showBackgroundColor]: boolean;
    };
    extendedStyles?: PromotionalLeftRightBannerStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const link = ownProps.fields.buttonLink !== undefined ? getLink(state, ownProps.fields.buttonLink) : undefined;
    const bannerLink =
        ownProps.fields.entireBannerLink !== undefined ? getLink(state, ownProps.fields.entireBannerLink) : undefined;
    return {
        bannerUrl: bannerLink?.url,
        url: link?.url,
        title: fields.buttonLabel,
    };
};

export interface PromotionalLeftRightBannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    childWrapperStyles: InjectableCss;
}

export const bannerStyles: PromotionalLeftRightBannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: ${getColor("common.white")};
            text-align: center;
        `,
    },
    childWrapperStyles: {
        css: css`
            max-width: 1400px;
            padding: 0 17px;
            margin: auto;
            width: 100%;
        `,
    },
};

const onClick = (history: History, link: string | undefined) => {
    if (link && link !== undefined) {
        if (link.startsWith("http")) {
            window.location.href = link;
        } else {
            history.push(link);
        }
    }
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

const PromotionalLeftRightBanner: React.FC<Props> = ({ fields, url, title, bannerUrl, history, extendedStyles }) => {
    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${fields.image});
           background-size: cover;`
            : `background-color: ${fields.backgroundColor};`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    const minimumHeightStyles = `min-height: ${fields.minimumHeight}px;`;

    let topBottomOverlayPositioningStyles;
    if (!fields.partialOverlay) {
        topBottomOverlayPositioningStyles = "align-items: stretch;";
    } else if (fields.partialOverlayPositioning === "top") {
        topBottomOverlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.partialOverlayPositioning === "middle") {
        topBottomOverlayPositioningStyles = "align-items: center;";
    } else if (fields.partialOverlayPositioning === "bottom") {
        topBottomOverlayPositioningStyles = "align-items: flex-end;";
    }

    const [styles] = React.useState(() => mergeToNew(bannerStyles, extendedStyles));

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${minimumHeightStyles}
            ${topBottomOverlayPositioningStyles}
            .content_promotional_banner_heading_mobile {
                display: none;
            }
            .content_promotional_banner_subheading_mobile {
                display: none;
            }
            .content_promotional_banner_mobile_image {
                display: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        background-image: none;
                        margin-bottom: 5px;
                        ${fields.showInTwoColumn ? "padding: 15px;" : "padding-bottom: 15px;"}
                        .content_promotional_banner_mobile_image {
                            display: block;
                            margin-left: ${fields.showInTwoColumn ? "0px" : "auto"};
                            ${fields.showInTwoColumn ? "" : "margin-right: auto;"};
                            ${fields.showInTwoColumn ? "max-width: 150px;" : ""}
                        }
                        ${fields.showBackgroundColor ? "background: #f3f6f4;" : ""}
                        .content_promotional_banner_heading_mobile {
                            display: block;
                        }
                        .content_promotional_banner_subheading_mobile {
                            display: block;
                        }
                        ${fields.showInTwoColumn
                            ? ".content_promotional_banner_parent { h4 {line-height: .65 !important} h1 {line-height: .6 !important} h1 span {font-size: 5vw} }"
                            : ""}
                        ${fields.showInTwoColumn
                            ? ".content_promotional_banner_subheading_mobile { ul li strong span {line-height: .6 !important} ul li span { font-size: 3vw !important; } li {font-size: 3vw !important;} }"
                            : ""}   
                        ${fields.showInTwoColumn
                            ? ".content_promotional_banner_button { min-height: 30px !important; span { font-size: 4vw !important }}"
                            : ""}
                        .content_promotional_banner_heading {
                            display: none;
                        }
                        .content_promotional_banner_subheading {
                            display: none;
                        }
                        color: ${getColor("common.black")};
                        ${fields.showInTwoColumn
                            ? ""
                            : ".content_promotional_banner_child { padding: 0px; } .content_promotional_banner_subheading_mobile { margin-left: 10px; } .content_promotional_banner_heading_mobile { margin-left: 10px; } .content_promotional_banner_button { margin-left: 10px; }"}
                    `,
                    css`
                        .content_promotional_banner_child {
                            margin: 15px;
                        }
                    `,
                    css`
                        .content_promotional_banner_child {
                            margin: 15px;
                        }
                    `,
                    css`
                        .content_promotional_banner {
                            max-width: auto;
                        }
                        .content_promotional_banner_child {
                            margin: auto;
                        }
                    `,
                    css`
                        .content_promotional_banner {
                            max-width: auto;
                        }
                        .content_promotional_banner_child {
                            margin: auto;
                            ${fields.showInTwoColumn ? "transform: translateY(-20%);" : ""}
                        }
                    `,
                ])}
        `,
    };

    let flexLeftRightOverlayPositioningStyles;
    if (fields.contentAlignment === "left") {
        flexLeftRightOverlayPositioningStyles = "justify-content: flex-start;";
    } else if (fields.contentAlignment === "right") {
        flexLeftRightOverlayPositioningStyles = "justify-content: flex-end;";
    } else {
        flexLeftRightOverlayPositioningStyles = "justify-content: center;";
    }

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            padding: ${fields.topContentPadding}% ${fields.rightContentPadding}% ${fields.bottomContentPadding}% ${fields.leftContentPadding}%;
            text-align: ${fields.contentAlignment};
            ${flexLeftRightOverlayPositioningStyles}
            ${breakpointMediaQueries(baseTheme, [
                css`
                    padding-right: 0 !important;
                    padding-left: 0 !important;
                    color: ${getColor("common.black")};
                `,
                css`
                    color: ${getColor("common.black")};
                    background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
                `,
                css`
                    background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
                `,
                css`
                    background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
                `,
                css`
                    background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
                `,
            ])}
        `,
    };

    let checkmarkPositioningStyles;
    let listItemPadding;
    if (fields.contentAlignment === "left") {
        checkmarkPositioningStyles = "left: 1em;";
        listItemPadding = "padding: 0.25em 0 0 3em;";
    } else if (fields.contentAlignment === "right") {
        checkmarkPositioningStyles = "left: auto; right: 1em;";
        listItemPadding = "padding: 0.25em 3em 0 0;";
    } else {
        checkmarkPositioningStyles = "left: 1em;";
        listItemPadding = "padding: 0.25em 3em 0 3em;";
    }

    const checkmarkStyles = {
        css: css`
            ul li:after {
                content: " ";
                display: block;
                width: 0.6em;
                height: 1.1em;
                border: solid ${fields.checkmarkColor};
                border-width: 0 0.2em 0.2em 0;
                position: absolute;
                ${checkmarkPositioningStyles}
                top: 22%;
                margin-top: -0.2em;
                -webkit-transform: rotate(45deg);
                -moz-transform: rotate(45deg);
                -o-transform: rotate(45deg);
                transform: rotate(45deg);
            }
            ul li {
                font-size: 15px;
                margin-bottom: 0.5em;
                list-style-type: none;
                ${listItemPadding}
                position: relative;
                text-align: ${fields.contentAlignment};
            }
            ul {
                list-style-type: none;
                padding: 0;
            }
            ${breakpointMediaQueries(baseTheme, [
                css`
                    ul li {
                        font-size: 15px;
                        margin-bottom: 0.5em;
                        list-style-type: none;
                        padding: 0.25em 0 0 3em;
                        position: relative;
                        flex: 0 0 50%;
                    }
                `,
                css`
                    ul li {
                        font-size: 15px;
                        margin-bottom: 0.5em;
                        list-style-type: none;
                        padding: 0.25em 0 0 3em;
                        position: relative;
                        flex: 0 0 50%;
                    }
                `,
                css`
                    margin-left: 0px;
                    margin-right: 0px;
                `,
                css`
                    margin-left: 0px;
                    margin-right: 0px;
                `,
                css`
                    margin-left: 0px;
                    margin-right: 0px;
                `,
            ])}
        `,
    };

    let showLink;
    if (!fields.entireBannerLink || fields.entireBannerLink.value === "") {
        showLink = false;
    } else {
        showLink = true;
    }

    let headingMobilePopulated;
    if (fields.headingMobile && fields.headingMobile !== "" && fields.headingMobile !== undefined) {
        headingMobilePopulated = true;
    } else {
        headingMobilePopulated = false;
    }

    let subHeadingMobilePopulated;
    if (fields.subheadingMobile && fields.subheadingMobile !== "" && fields.subheadingMobile !== undefined) {
        subHeadingMobilePopulated = true;
    } else {
        subHeadingMobilePopulated = false;
    }

    return (
        <>
            {!fields.showInTwoColumn ? (
                <StyledWrapper {...wrapperStyles} className="content_promotional_banner">
                    <StyledWrapper {...styles.childWrapperStyles} className="content_promotional_banner_child">
                        <Img
                            src={fields.smallImage}
                            style={{ maxHeight: "200px", maxWidth: "100%" }}
                            className="content_promotional_banner_mobile_image"
                        />
                        {showLink && (
                            <Link
                                style={{ height: "100%", width: "100%" }}
                                href={bannerUrl}
                                className="content_promotional_banner_link"
                            >
                                <StyledWrapper {...overlayWrapperStyles} className="content_promotional_banner_parent">
                                    <Typography className="content_promotional_banner_heading">
                                        {parse(fields.heading, parserOptions)}
                                    </Typography>
                                    <StyledWrapper
                                        className="content_promotional_banner_subheading"
                                        {...checkmarkStyles}
                                    >
                                        {parse(fields.subheading, parserOptions)}
                                    </StyledWrapper>
                                    <Typography className="content_promotional_banner_heading_mobile">
                                        {headingMobilePopulated && parse(fields.headingMobile, parserOptions)}
                                        {!headingMobilePopulated && parse(fields.heading, parserOptions)}
                                    </Typography>
                                    <StyledWrapper
                                        className="content_promotional_banner_subheading_mobile"
                                        {...checkmarkStyles}
                                    >
                                        {subHeadingMobilePopulated
                                            ? parse(fields.subheadingMobile, parserOptions)
                                            : parse(fields.subheading, parserOptions)}
                                    </StyledWrapper>
                                    {!fields.disableButton && (
                                        <Button
                                            className={`content_promotional_banner_button ${fields.buttonClassName}`}
                                            {...styles.bannerButton}
                                            variant={fields.variant}
                                            onClick={() => onClick(history, url)}
                                            // XNG-Change TSC-173 adding className to the inner typography in favor for GA4
                                            {...{ typographyProps: { className: fields.buttonClassName } as any }}
                                        >
                                            {fields.buttonLabel || title || url}
                                        </Button>
                                    )}
                                </StyledWrapper>
                            </Link>
                        )}
                        {!showLink && (
                            <StyledWrapper className="content_promotional_banner_parent" {...overlayWrapperStyles}>
                                <Typography className="content_promotional_banner_heading">
                                    {parse(fields.heading, parserOptions)}
                                </Typography>
                                <StyledWrapper className="content_promotional_banner_subheading" {...checkmarkStyles}>
                                    {parse(fields.subheading, parserOptions)}
                                </StyledWrapper>
                                <Typography className="content_promotional_banner_heading_mobile">
                                    {headingMobilePopulated
                                        ? parse(fields.headingMobile, parserOptions)
                                        : parse(fields.heading, parserOptions)}
                                </Typography>
                                <StyledWrapper
                                    className="content_promotional_banner_subheading_mobile"
                                    {...checkmarkStyles}
                                >
                                    {subHeadingMobilePopulated
                                        ? parse(fields.subheadingMobile, parserOptions)
                                        : parse(fields.subheading, parserOptions)}
                                </StyledWrapper>
                                {!fields.disableButton && (
                                    <Button
                                        className={`content_promotional_banner_button ${fields.buttonClassName}`}
                                        {...styles.bannerButton}
                                        variant={fields.variant}
                                        onClick={() => onClick(history, url)}
                                        // XNG-Change TSC-173 adding className to the inner typography in favor for GA4
                                        {...{ typographyProps: { className: fields.buttonClassName } as any }}
                                    >
                                        {fields.buttonLabel || title || url}
                                    </Button>
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <GridContainer {...wrapperStyles} style={{ maxWidth: "100%" }} className="content_promotional_banner">
                    <GridItem width={[7, 12, 12, 12, 12, 12]}>
                        <Img
                            src={fields.smallImage}
                            style={{ maxWidth: "175px", maxHeight: "300px" }}
                            className="content_promotional_banner_mobile_image"
                        />
                    </GridItem>
                    <GridItem
                        width={[5, 12, 12, 12, 12, 12]}
                        {...styles.childWrapperStyles}
                        style={{ padding: "0 15px" }}
                        className="content_promotional_banner_child"
                    >
                        {showLink && (
                            <Link
                                style={{ height: "100%", width: "100%" }}
                                href={bannerUrl}
                                className="content_promotional_banner_link"
                            >
                                <StyledWrapper {...overlayWrapperStyles} className="content_promotional_banner_parent">
                                    <Typography className="content_promotional_banner_heading">
                                        {parse(fields.heading, parserOptions)}
                                    </Typography>
                                    <StyledWrapper
                                        className="content_promotional_banner_subheading"
                                        {...checkmarkStyles}
                                    >
                                        {parse(fields.subheading, parserOptions)}
                                    </StyledWrapper>
                                    <Typography className="content_promotional_banner_heading_mobile">
                                        {headingMobilePopulated
                                            ? parse(fields.headingMobile, parserOptions)
                                            : parse(fields.heading, parserOptions)}
                                    </Typography>
                                    <StyledWrapper
                                        className="content_promotional_banner_subheading_mobile"
                                        {...checkmarkStyles}
                                    >
                                        {subHeadingMobilePopulated
                                            ? parse(fields.subheadingMobile, parserOptions)
                                            : parse(fields.subheading, parserOptions)}
                                    </StyledWrapper>
                                    {!fields.disableButton && (
                                        <Button
                                            className={`content_promotional_banner_button ${fields.buttonClassName}`}
                                            {...styles.bannerButton}
                                            variant={fields.variant}
                                            onClick={() => onClick(history, url)}
                                            // XNG-Change TSC-173 adding className to the inner typography in favor for GA4
                                            {...{ typographyProps: { className: fields.buttonClassName } as any }}
                                        >
                                            {fields.buttonLabel || title || url}
                                        </Button>
                                    )}
                                </StyledWrapper>
                            </Link>
                        )}
                        {!showLink && (
                            <StyledWrapper className="content_promotional_banner_parent" {...overlayWrapperStyles}>
                                <Typography className="content_promotional_banner_heading">
                                    {parse(fields.heading, parserOptions)}
                                </Typography>
                                <StyledWrapper className="content_promotional_banner_subheading" {...checkmarkStyles}>
                                    {parse(fields.subheading, parserOptions)}
                                </StyledWrapper>
                                <Typography className="content_promotional_banner_heading_mobile">
                                    {headingMobilePopulated
                                        ? parse(fields.headingMobile, parserOptions)
                                        : parse(fields.heading, parserOptions)}
                                </Typography>
                                <StyledWrapper
                                    className="content_promotional_banner_subheading_mobile"
                                    {...checkmarkStyles}
                                >
                                    {subHeadingMobilePopulated
                                        ? parse(fields.subheadingMobile, parserOptions)
                                        : parse(fields.subheading, parserOptions)}
                                </StyledWrapper>
                                {!fields.disableButton && (
                                    <Button
                                        className={`content_promotional_banner_button ${fields.buttonClassName}`}
                                        {...styles.bannerButton}
                                        variant={fields.variant}
                                        onClick={() => onClick(history, url)}
                                        // XNG-Change TSC-173 adding className to the inner typography in favor for GA4
                                        {...{ typographyProps: { className: fields.buttonClassName } as any }}
                                    >
                                        {fields.buttonLabel || title || url}
                                    </Button>
                                )}
                            </StyledWrapper>
                        )}
                    </GridItem>
                </GridContainer>
            )}{" "}
        </>
    );
};

const promotionalLeftRightBanner: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(PromotionalLeftRightBanner)),
    definition: {
        group: "Basic",
        icon: "Banner",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonClassName,
                editorTemplate: "TextField",
                displayName: "Button Class Name",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.entireBannerLink,
                displayName: "Entire Banner Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.partialOverlay,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.partialOverlayPositioning,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                hideEmptyOption: true,
                defaultValue: "bottom",
                isVisible: widget => widget.fields.partialOverlay,
            },
            {
                fieldType: "General",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "white",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
            {
                fieldType: "Translatable",
                name: fields.headingMobile,
                editorTemplate: "RichTextField",
                displayName: "Mobile-Only Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
            {
                fieldType: "General",
                name: fields.checkmarkColor,
                editorTemplate: "ColorPickerField",
                displayName: "Checkmark Color",
                defaultValue: "black",
            },
            {
                name: fields.subheading,
                displayName: "Sub Heading Text",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Delimited list of bullet points.",
            },
            {
                name: fields.subheadingMobile,
                displayName: "Mobile-Only Sub Heading Text",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Delimited list of bullet points.",
            },
            {
                fieldType: "General",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
            },
            {
                fieldType: "General",
                name: fields.topContentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 1,
                tooltip: "Percentage padding value",
            },
            {
                fieldType: "General",
                name: fields.rightContentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tooltip: "Percentage padding value",
            },
            {
                fieldType: "General",
                name: fields.bottomContentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 1,
                tooltip: "Percentage padding value",
            },
            {
                fieldType: "General",
                name: fields.leftContentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tooltip: "Percentage padding value",
            },
            {
                name: fields.contentAlignment,
                displayName: "Content Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                defaultValue: "left",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.showInTwoColumn,
                editorTemplate: "CheckboxField",
                displayName: "Show as two-column in mobile view",
                defaultValue: false,
            },
            {
                fieldType: "Translatable",
                name: fields.smallImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.showBackgroundColor,
                editorTemplate: "CheckboxField",
                displayName: "Show light gray backdrop in mobile view",
                defaultValue: false,
            },
        ],
    },
};

export default promotionalLeftRightBanner;
